import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiCheckCircle, FiAlertCircle, FiExternalLink, FiCalendar, FiPackage } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import IconWrapper from '../components/print-steps/IconWrapper';
import { fetchLatestRelease, findWindowsInstaller, formatReleaseNotes, GitHubRelease } from '../lib/supabase/api/github-service';

const SoftwarePage: React.FC = () => {
  const navigate = useNavigate();
  const [downloadStatus, setDownloadStatus] = useState<'initial' | 'started' | 'failed'>('initial');
  const [latestRelease, setLatestRelease] = useState<GitHubRelease | null>(null);
  const [loadingRelease, setLoadingRelease] = useState(true);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  // Fetch latest release on component mount
  useEffect(() => {
    const fetchRelease = async () => {
      try {
        setLoadingRelease(true);
        setReleaseError(null);
        const release = await fetchLatestRelease('ctrlp-zb', 'shop-dashboard-releases');
        if (release) {
          setLatestRelease(release);
        } else {
          setReleaseError('Failed to fetch latest release');
        }
      } catch (error) {
        console.error('Error fetching release:', error);
        setReleaseError('Failed to fetch latest release');
      } finally {
        setLoadingRelease(false);
      }
    };

    fetchRelease();
  }, []);

  // Get download URL from latest release
  const getDownloadUrl = (): string => {
    if (!latestRelease) return '';
    const installer = findWindowsInstaller(latestRelease);
    return installer?.browser_download_url || '';
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Trigger download automatically when the page loads and release is loaded
  useEffect(() => {
    if (!loadingRelease && latestRelease && getDownloadUrl()) {
      const startDownload = () => {
        setDownloadStatus('started');
        try {
          const link = document.createElement('a');
          link.href = getDownloadUrl();
          link.setAttribute('download', `CtrlP-Setup-${latestRelease.tag_name}.exe`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error("Download failed:", error);
          setDownloadStatus('failed');
        }
      };

      // Start download after a short delay
      const timer = setTimeout(() => {
        startDownload();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [loadingRelease, latestRelease]);

  // Handle manual download button click
  const handleDownload = () => {
    const url = getDownloadUrl();
    if (url) {
      setDownloadStatus('started');
      window.location.href = url;
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />
      
      <main className="flex-grow container mx-auto py-12 px-4 flex items-center justify-center">
        <div className="max-w-4xl w-full">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden p-10">
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                <IconWrapper icon={FiDownload} className="h-12 w-12 text-blue-600" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              CTRLP Shopkeeper Dashboard
            </h1>

            <p className="text-center text-gray-600 mb-8 text-lg">
              Thank you for joining the CTRLP network.
            </p>

            {/* Release Information */}
            {loadingRelease ? (
              <div className="mb-8 max-w-md mx-auto">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
                    <p className="text-gray-700">Loading latest release...</p>
                  </div>
                </div>
              </div>
            ) : releaseError ? (
              <div className="mb-8 max-w-md mx-auto">
                <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                  <div className="flex items-center">
                    <IconWrapper icon={FiAlertCircle} className="text-red-500 w-5 h-5 mr-2" />
                    <p className="text-gray-700">{releaseError}</p>
                  </div>
                </div>
              </div>
            ) : latestRelease ? (
              <div className="mb-8 max-w-2xl mx-auto">
                <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <IconWrapper icon={FiPackage} className="text-green-600 w-5 h-5 mr-2" />
                      <h3 className="text-lg font-semibold text-green-800">
                        Latest Release: {latestRelease.tag_name}
                      </h3>
                    </div>
                    <a
                      href={latestRelease.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-green-600 hover:text-green-700 text-sm"
                    >
                      <IconWrapper icon={FiExternalLink} className="w-4 h-4 mr-1" />
                      View on GitHub
                    </a>
                  </div>

                  <div className="flex items-center text-sm text-green-700 mb-3">
                    <IconWrapper icon={FiCalendar} className="w-4 h-4 mr-1" />
                    <span>Released on {formatDate(latestRelease.published_at)}</span>
                  </div>

                  {(() => {
                    const installer = findWindowsInstaller(latestRelease);
                    return installer ? (
                      <div className="text-sm text-green-700">
                        <span className="font-medium">Download:</span> {installer.name} ({formatFileSize(installer.size)})
                      </div>
                    ) : null;
                  })()}

                  {latestRelease.body && (
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <h4 className="text-sm font-medium text-green-800 mb-2">Release Notes:</h4>
                      <div
                        className="text-sm text-green-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: formatReleaseNotes(latestRelease.body) }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Download Status */}
            <div className="mb-8 max-w-md mx-auto">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                {downloadStatus === 'started' ? (
                  <div className="flex items-center mb-2">
                    <IconWrapper icon={FiCheckCircle} className="text-green-500 w-5 h-5 mr-2" />
                    <p className="text-gray-700 font-medium">Download initiated!</p>
                  </div>
                ) : downloadStatus === 'failed' ? (
                  <div className="flex items-center mb-2">
                    <IconWrapper icon={FiAlertCircle} className="text-amber-500 w-5 h-5 mr-2" />
                    <p className="text-gray-700 font-medium">Download couldn't start automatically</p>
                  </div>
                ) : null}
                <p className="text-gray-600">
                  {downloadStatus === 'started'
                    ? "If you don't see the download in progress, please try the button below."
                    : latestRelease
                      ? "Your download should start automatically in a moment. If it doesn't begin, please use the button below."
                      : "Loading release information..."}
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleDownload}
                disabled={!latestRelease || !getDownloadUrl()}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3 text-white font-medium rounded-lg flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconWrapper icon={FiDownload} className="mr-2" />
                {latestRelease ? `Download ${latestRelease.tag_name}` : 'Download Software'}
              </Button>
            </div>

            <div className="mt-10 text-sm text-gray-500 pt-6 border-t border-gray-100 text-center">
              <p>Always downloads the latest version automatically</p>
              <p className="mt-1">For support, please contact: <a href="mailto:support@ctrlp.co.in" className="text-blue-600 hover:underline">support@ctrlp.co.in</a></p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SoftwarePage;
